import type { Store } from '@wiztime/storage';
import type { DerivedKeys, SyncManager, SyncStatus } from '@wiztime/sync';
// The configuration module alone: the rest of the sync package is loaded on first use (see `load`).
import {
  createStorageConfigStore,
  createStorageLastSpaceStore,
  markFullPushPending,
} from '@wiztime/sync/config';
import { readStorage, removeStorage, writeStorage } from '../lib/safeStorage';
import { SyncUiState } from '../lib/syncState';
import { askForeignLocalData } from '../features/sync/foreignDataPrompt';
import { leaveLocalMode } from '../lib/localMode';
import { resetDeviceAfterClear } from '../lib/resetAfterClear';
import { loadLocale } from './locale';
import { StorageKey } from '../lib/storageKeys';

/**
 * The sync feature as the app sees it. The sync code (key derivation, word list, HTTP) is large
 * and most sessions never use it, so it is loaded on first use: at start-up only when this device
 * was set up for sync earlier, otherwise when the user opens the sync settings.
 */
export interface AppSync {
  /** Latest status; `state` is `SyncUiState.Disabled` until a manager is loaded and configured. */
  getStatus(): SyncStatus;
  subscribe(listener: () => void): () => void;
  /**
   * Whether this device holds a sync configuration. It is read from the device storage, so it is
   * true before the sync code has loaded, when loading failed, and in a tab opened before another
   * tab turned sync on (`getStatus` says `Disabled` in all three cases).
   */
  isConfigured(): boolean;
  /**
   * Whether this device joined an existing space and has not finished reading it yet (the stored `pullFirst`
   * flag). It survives a reload, so the "Syncing data…" wait does not depend on memory alone.
   */
  isFirstSyncPending(): boolean;
  /** Load the sync code and return the manager. */
  manager(): Promise<SyncManager>;
  /**
   * Resume sync at app start when this device was set up for it, and follow changes other tabs
   * make to the configuration. Never throws.
   */
  start(): Promise<void>;
  /**
   * Make the next sync push every local record (after importing a file). The request is written
   * to the stored configuration, so it holds even when the sync code is not running. No-op while
   * sync is off.
   */
  requestFullPush(): void;
  /**
   * Derive the keys of a freshly generated passphrase ahead of time and resolve with the first 8
   * characters of its space identifier. `enableSync` then reuses the result, so Argon2 runs once.
   */
  previewSpaceId(passphrase: string): Promise<string>;
  /** Forget a previewed derivation (the user left the screen without enabling sync). */
  discardPreview(): void;
}

const DISABLED_STATUS: SyncStatus = {
  state: SyncUiState.Disabled,
  lastSyncAt: null,
  lastError: null,
  skippedRecords: 0,
  retryAt: null,
};

export const SPACE_ID_PREFIX_CHARS = 8;

export function isSyncEnabled(status: SyncStatus): boolean {
  return status.state !== SyncUiState.Disabled;
}

function isConfiguredOnDevice(): boolean {
  return readStorage(StorageKey.Sync) !== null;
}

/**
 * The sync configuration in the device storage, through the storage helpers that never throw
 * (private windows and blocked site data read as "not configured"). It uses the configuration
 * module of the sync package, so the format of data dictionary section 7 has one definition, and it
 * works without loading the rest of the sync code.
 */
const configStore = createStorageConfigStore({
  getItem: readStorage,
  setItem: (key, value) => {
    writeStorage(key, value);
  },
  removeItem: removeStorage,
});

/** The space this device last synced with, in the same never-throwing storage (data dictionary section 7). */
const lastSpaceStore = createStorageLastSpaceStore(
  {
    getItem: readStorage,
    setItem: (key, value) => {
      writeStorage(key, value);
    },
    removeItem: removeStorage,
  },
  StorageKey.LastSpaceId,
);

export function createAppSync(store: Store): AppSync {
  let status: SyncStatus = DISABLED_STATUS;
  let loaded: Promise<SyncManager> | null = null;
  let current: SyncManager | null = null;
  const listeners = new Set<() => void>();
  const previews = new Map<string, Promise<DerivedKeys>>();

  const notify = (): void => {
    for (const listener of [...listeners]) listener();
  };
  const setStatus = (next: SyncStatus): void => {
    status = next;
    notify();
  };
  let listeningToStorage = false;

  function load(): Promise<SyncManager> {
    loaded ??= import('@wiztime/sync').then(({ createSyncManager, deriveKeysInWorker }) => {
      const manager = createSyncManager({
        store,
        lastSpaceStore,
        // Data of another account on this device: ask the user before it can flow into the new space.
        onForeignLocalData: askForeignLocalData,
        onLocalDataCleared: resetDeviceAfterClear,
        // Registering, logging in or enabling sync succeeded: the device is no longer local-only.
        onSyncActivated: leaveLocalMode,
        // The language of the mails the server sends (verification, email change).
        locale: loadLocale,
        // A previewed derivation is used once, so the keys of a discarded passphrase do not linger.
        deriveKeys: (passphrase) => {
          const preview = previews.get(passphrase);
          if (preview === undefined) return deriveKeysInWorker(passphrase);
          previews.delete(passphrase);
          return preview;
        },
        logger: {
          debug: () => undefined,
          info: () => undefined,
          warn: (message, context) => console.warn(`[sync] ${message}`, context ?? ''),
          error: (message, context) => console.error(`[sync] ${message}`, context ?? ''),
        },
      });
      manager.subscribe(setStatus);
      current = manager;
      return manager;
    });
    loaded.catch(() => {
      loaded = null;
    });
    return loaded;
  }

  async function resume(): Promise<void> {
    if (!isConfiguredOnDevice()) return;
    try {
      await (await load()).start();
    } catch (error) {
      console.error('[sync] could not resume sync', error);
    }
  }

  /** Another tab of this app changed the sync configuration: follow it. */
  function onStorage(event: StorageEvent): void {
    if (event.key !== null && event.key !== StorageKey.Sync) return;
    if (isConfiguredOnDevice()) void resume();
    else void current?.stop().catch((error: unknown) => console.error('[sync] could not stop sync', error));
    notify();
  }

  return {
    getStatus: () => status,
    isConfigured: isConfiguredOnDevice,
    isFirstSyncPending: () => configStore.load()?.pullFirst === true,
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    manager: load,
    async start() {
      if (!listeningToStorage && typeof window !== 'undefined') {
        listeningToStorage = true;
        window.addEventListener('storage', onStorage);
      }
      await resume();
    },
    requestFullPush() {
      if (current === null) {
        markFullPushPending(configStore);
        void resume();
        return;
      }
      current.requestFullPush();
      void current.syncNow();
    },
    async previewSpaceId(passphrase) {
      const { deriveKeysInWorker, normalizePassphrase } = await import('@wiztime/sync');
      const normalized = normalizePassphrase(passphrase);
      const derivation = previews.get(normalized) ?? deriveKeysInWorker(normalized);
      previews.set(normalized, derivation);
      try {
        return (await derivation).spaceId.slice(0, SPACE_ID_PREFIX_CHARS);
      } catch (error) {
        previews.delete(normalized);
        throw error;
      }
    },
    discardPreview: () => previews.clear(),
  };
}
